import { useEffect, useState } from "react";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { Stack } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { InvestigationButton } from "../components/InvestigationButton";
import { EGBasicSelect } from "../../../../MainComponents/Select/EGBasicSelect";
import { useConstants } from "../Main/useConstants";
import { getPoliciesCatalogue } from "../../../../../helpers/policiesCatalogue";
import { resolveCatalogueLabel } from "../../../../../helpers/catalogueLabel";
import { returnIsValid } from "../Main/useConstants";
import { EvaluationList } from "./EvaluationList";
import { initEvaluation } from "./Evaluation";

// Funcion revisar el formulario y retornar si es valido y cuales son los errores
const validateForm = (
  evaluations, 
  causes, 
  switchPolicies, 
  switchMeasures, 
  switchActions, 
  switchCauses,
  newPolicyTextField,
  newMeasureTextField,
  newActionTextField,
  newCauseTextField,
) => {
  const { priority, policies, measures, actions } = evaluations;
  const errors = {};
  if (priority == null) {
    errors.priority = true;
  }
  if (switchPolicies) {
    if (policies.list.length === 0 && newPolicyTextField.value.trim() === "") {
      errors.newPolicy = true;
    } else {
      const errorsPolicies = [];
      policies.list.forEach((item, idx) => {
        if (item.value.trim() === "") {
          errorsPolicies.push(idx);
        }
      });
      if (errorsPolicies.length > 0) {
        errors.policies = errorsPolicies;
      }
    }
  }
  if (switchMeasures) {
    if (measures.list.length === 0 && newMeasureTextField.value.trim() === "") {
      errors.newMeasure = true;
    } else {
      const errorsMeasures = [];
      measures.list.forEach((item, idx) => {
        if (item.value.trim() === "") {
          errorsMeasures.push(idx);
        }
      });
      if (errorsMeasures.length > 0) {
        errors.measures = errorsMeasures;
      }
    }
  }
  if (switchActions) {
    if (actions.list.length === 0 && newActionTextField.value.trim() === "") {
      errors.newAction = true;
    } else {
      const errorsActions = [];
      actions.list.forEach((item, idx) => {
        if (item.value.trim() === "") {
          errorsActions.push(idx);
        }
      });
      if (errorsActions.length > 0) {
        errors.actions = errorsActions;
      }
    }
  }
  if (switchCauses) {
    if (causes.length === 0 && newCauseTextField.value.trim() === "") {
      errors.newCause = true;
    } else {
      const errorsCauses = [];
      causes.forEach((item, idx) => {
        if (item.reason.trim() === "") {
          errorsCauses.push(idx);
        }
      });
      if (errorsCauses.length > 0) {
        errors.causes = errorsCauses;
      }
    }
  }
  return [
    Object.entries(errors).length === 0 ? true : false,
    errors
  ];
}

// Evaluacion para la edición
export const AddEvaluation = ({
  investigation,
  handleSaveEvaluations,
  setIsEditing,
}) => {

  const [t] = useTranslation("investigation");
  const { evaluation: { priorityOpts } } = useConstants();
  const [evaluations, setEvaluations] = useState(investigation.evaluation ?? _.cloneDeep(initEvaluation));
  const [causes, setCauses] = useState(investigation.causes ?? []);
  const [switches, setSwitches] = useState({
    policies: evaluations.policies.list.length > 0 ? true : false,
    measures: evaluations.measures.list.length > 0 ? true : false,
    actions: evaluations.actions.list.length > 0 ? true : false,
    causes: causes.length > 0 ? true : false,
  });

  const [triedToSave, setTriedToSave] = useState(false);
  const [errors, setErrors] = useState({});

  const [newPolicy, setNewPolicy] = useState({ value: "", details: "", path: "" });
  const policiesCatalogue = getPoliciesCatalogue();
  const [newMeasure, setNewMeasure] = useState({ value: "", details: "" });
  const [newAction, setNewAction] = useState({ value: "", details: "" });
  const [newCause, setNewCause] = useState({ value: "", details: "" });

  useEffect(() => {
    if (triedToSave) {
      const [_, errors] = validateForm(
        evaluations, causes, 
        switches.policies, 
        switches.measures, 
        switches.actions, 
        switches.causes,
        newPolicy,
        newMeasure,
        newAction,
        newCause,
      );
      setErrors(errors);
    }
  }, [evaluations, causes, newPolicy, newMeasure, newAction, newCause]);

  // Acciones, politicas y medidas ------------------------------------------------------------------------------------------------

  const handleChangeList = (id, idx, value) => { // Para editar los items ya agregados
    setEvaluations(prev=>{
      const newEvaluations = _.cloneDeep(prev);
      _.set(newEvaluations, `${id}.list.${idx}.value`, value);
      return newEvaluations;
    });
  }

  // Cambio desde un catalogo: llega la ruta y se guarda junto a su etiqueta.
  const handleCatalogueChangeList = async (id, idx, path) => {
    const label = await resolveCatalogueLabel({ ...policiesCatalogue, path });
    setEvaluations(prev=>{
      const newEvaluations = _.cloneDeep(prev);
      _.set(newEvaluations, `${id}.list.${idx}.path`, path ?? "");
      _.set(newEvaluations, `${id}.list.${idx}.value`, label);
      return newEvaluations;
    });
  }

  const handleChangueDetailsList = (id, idx, value) => { // Para editar los items ya agregados
    setEvaluations(prev=>{
      const newEvaluations = _.cloneDeep(prev);
      _.set(newEvaluations, `${id}.list.${idx}.details`, value);
      return newEvaluations;
    });
  }

  const handleDeleteFromList = (id, idx) => { // Para borrar item ya agregado
    setEvaluations(prev=>{
      const newEvaluations = _.cloneDeep(prev);
      newEvaluations[id].list.splice(idx, 1);
      return newEvaluations;
    });
  }

  const handleAddToList = (id, newItem) => { // Para agregar un nuevo item
    setEvaluations(prev=>{
      const newEvaluations = _.cloneDeep(prev);
      newEvaluations[id].list.push(newItem);
      return newEvaluations;
    });
  }

  // Causas ------------------------------------------------------------------------------------------------

  const handleChangueCauses = (id, idx, value) => {
    setCauses(prev=>{
      const newCauses = _.cloneDeep(prev);
      _.set(newCauses, `${idx}.reason`, value);
      return newCauses;
    });
  }

  const handleChangueDetailsCauses = (id, idx, value) => { // Para editar los items ya agregados
    setCauses(prev=>{
      const newCauses = _.cloneDeep(prev);
      _.set(newCauses, `${idx}.details`, value);
      return newCauses;
    });
  }

  const handleDeleteFromCauses = (id, idx) => { // Para borrar item ya agregado
    setCauses(prev=>{
      const newCauses = _.cloneDeep(prev);
      newCauses.splice(idx, 1);
      return newCauses;
    });
  }

  const handleAddToCauses = (id, newItem) => { // Para agregar un nuevo item
    const itemFormatted = {
      reason: newItem.value,
      details: newItem.details,
    }
    setCauses(prev=>{
      const newCauses = _.cloneDeep(prev);
      newCauses.push(itemFormatted);
      return newCauses;
    });
  }

  // Guardar evaluaciones y causas
  const handleSave = async() => {
    const [isValid, errors] = validateForm(
      evaluations,
      causes,
      switches.policies,
      switches.measures,
      switches.actions,
      switches.causes,
      newPolicy,
      newMeasure,
      newAction,
      newCause
    );
    setErrors(errors);
    setTriedToSave(true);
    if (!isValid) {
      enqueueSnackbar({ variant: "error", message: t("missing_required_inputs") });
      return;
    };

    // Cuando el switch esta en false se debe poner un array vacio en el campo list
    let filtredEvaluations = _.cloneDeep(evaluations);
    let filtredCauses = _.cloneDeep(causes);
    // Agregar la evaluacion si el input de nueva evaluacion no esta vacio
    if (newPolicy.value.trim() !== "") {
      filtredEvaluations.policies.list.push({
        value: newPolicy.value,
        details: newPolicy.details,
        path: newPolicy.path ?? "",
      });
    }
    if (newMeasure.value.trim() !== "") {
      filtredEvaluations.measures.list.push({ value: newMeasure.value, details: newMeasure.details });
    }
    if (newAction.value.trim() !== "") {
      filtredEvaluations.actions.list.push({ value: newAction.value, details: newAction.details });
    }
    if (newCause.value.trim() !== "") {
      filtredCauses.push({ reason: newCause.value, details: newCause.details });
    }

    // Si el switch esta en false se debe poner un array vacio en el campo list
    if (!switches.policies) {
      filtredEvaluations.policies.list = [];
    }
    if (!switches.measures) {
      filtredEvaluations.measures.list = [];
    }
    if (!switches.actions) {
      filtredEvaluations.actions.list = [];
    }
    if (!switches.causes) {
      filtredCauses = [];
    }
    try {
      await handleSaveEvaluations(filtredEvaluations, filtredCauses);
      setIsEditing(false);
    } catch (error) {
      console.error(error);
    }
  }

  return (  
    <Stack spacing={1.6} padding={2}>
      {/* Priority */}
      <EGBasicSelect
        label={t("evaluation.investigation_priority_question")}
        options={priorityOpts}
        value={evaluations.priority}
        onChange={(e) => setEvaluations({...evaluations, priority: e.target.value})}
        isValid={returnIsValid(errors.priority, triedToSave)}
        showSuccessValidation={true}
        showHelpText={false}
        required={true}
        sx={{ maxWidth: 540 }}
      />

      <EvaluationList
        id={"policies"}
        data={evaluations.policies.list}
        questionTitle={t("evaluation.policies_question_title")}
        questionLabel={t("evaluation.policies_question_label")}
        switchh={switches.policies}
        setSwitch={setSwitches}
        newItemError={errors.newPolicy}
        errors={errors.policies}
        triedToSave={triedToSave}
        handleChangeList={handleChangeList}
        handleChangueDetailsList={handleChangueDetailsList}
        handleDeleteFromList={handleDeleteFromList}
        handleAddToList={handleAddToList}
        setOutsideValue={setNewPolicy}
        catalogue={policiesCatalogue}
        onCatalogueChange={handleCatalogueChangeList}
      />

      <EvaluationList
        id={"measures"}
        data={evaluations.measures.list}
        questionTitle={t("evaluation.measures_question_title")}
        questionLabel={t("evaluation.measures_question_label")}
        switchh={switches.measures}
        setSwitch={setSwitches}
        newItemError={errors.newMeasure}
        errors={errors.measures}
        triedToSave={triedToSave}
        handleChangeList={handleChangeList}
        handleChangueDetailsList={handleChangueDetailsList}
        handleDeleteFromList={handleDeleteFromList}
        handleAddToList={handleAddToList}
        setOutsideValue={setNewMeasure}
      />

      <EvaluationList
        id={"actions"}
        data={evaluations.actions.list}
        questionTitle={t("evaluation.actions_question_title")}
        questionLabel={t("evaluation.actions_question_label")}
        switchh={switches.actions}
        setSwitch={setSwitches}
        newItemError={errors.newAction}
        errors={errors.actions}
        triedToSave={triedToSave}
        handleChangeList={handleChangeList}
        handleChangueDetailsList={handleChangueDetailsList}
        handleDeleteFromList={handleDeleteFromList}
        handleAddToList={handleAddToList}
        setOutsideValue={setNewAction}
      />

      <EvaluationList
        id={"causes"}
        data={causes}
        questionTitle={t("evaluation.cause_question_title")}
        questionLabel={t("evaluation.cause_question_label")}
        switchh={switches.causes}
        setSwitch={setSwitches}
        newItemError={errors.newCause}
        errors={errors.causes}
        triedToSave={triedToSave}
        handleChangeList={handleChangueCauses}
        handleChangueDetailsList={handleChangueDetailsCauses}
        handleDeleteFromList={handleDeleteFromCauses}
        handleAddToList={handleAddToCauses}
        setOutsideValue={setNewCause}
      />

      <Stack direction={{ xs: "column", md: "row" }} justifyContent="flex-end" spacing={1}>
        <InvestigationButton
          label={t("evaluation.cancel")}
          icon="close_icon"
          size="medium"
          onClick={() => setIsEditing(false)}
          />
        <InvestigationButton 
          label={t("evaluation.save")}
          size="medium"
          icon="mark_check_bold"
          variant="success"
          onClick={handleSave}
        />
      </Stack>

    </Stack>
  )
}